<script lang="ts">
import GuestLayout from "@/layouts/GuestLayout.vue"

export default { layout: GuestLayout }
</script>

<script setup lang="ts">
import { Head, router } from "@inertiajs/vue3"
import { ref, useTemplateRef } from "vue"
import { z } from "zod"

import DevToolsButton from "@/components/dev/DevToolsButton.vue"
import DevToolsPopover from "@/components/dev/DevToolsPopover.vue"
import FlashAlerts from "@/components/FlashAlerts.vue"
import { newSessionPath, passwordPath } from "@/routes"

const props = defineProps<{
  token: string
}>()

const loading = ref(false)
const authFormRef = useTemplateRef("authFormRef")

const schema = z
  .object({
    password: z.string().min(8, "Password must be at least 8 characters"),
    password_confirmation: z.string().min(1, "Please confirm your password"),
  })
  .refine((data) => data.password === data.password_confirmation, {
    message: "Passwords don't match",
    path: ["password_confirmation"],
  })

function populateTestPassword() {
  const s = authFormRef.value?.state as z.infer<typeof schema> | undefined
  if (s) {
    s.password = "password123"
    s.password_confirmation = "password123"
  }
}

function submit() {
  if (!authFormRef.value) return
  loading.value = true
  router.patch(passwordPath(props.token), authFormRef.value.state, {
    onFinish: () => {
      loading.value = false
    },
  })
}
</script>

<template>
  <Head title="Reset Password" />
  <UAuthForm
    ref="authFormRef"
    title="Reset your password"
    description="Enter your new password below."
    :schema="schema"
    :fields="[
      {
        name: 'password',
        label: 'New Password',
        type: 'password',
        required: true,
        autocomplete: 'new-password',
      },
      {
        name: 'password_confirmation',
        label: 'Confirm Password',
        type: 'password',
        required: true,
        autocomplete: 'new-password',
      },
    ]"
    :loading="loading"
    :submit="{ label: 'Reset Password' }"
    @submit="submit"
  >
    <template #validation>
      <FlashAlerts />
    </template>

    <template #footer>
      <UButton :to="newSessionPath()" variant="link" label="Back to sign in" class="text-muted" />
      <DevToolsPopover>
        <DevToolsButton label="Populate test password" @click="populateTestPassword" />
      </DevToolsPopover>
    </template>
  </UAuthForm>
</template>
